import React, { useState, useEffect } from 'react';
import { slaService } from '../services/api';
import { useSidebar } from '../context/SidebarContext';
import { usePageTitle } from '../context/PageTitleContext';

const SLAManagement = () => {
  const { isCollapsed } = useSidebar();
  const { setPageTitle, setPageSubtitle } = usePageTitle();

  const [slaRules, setSlaRules] = useState([]);
  const [ratingThresholds, setRatingThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [editingThreshold, setEditingThreshold] = useState(null);
  const [ruleForm, setRuleForm] = useState({});
  const [thresholdForm, setThresholdForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    setPageTitle('SLA Management');
    setPageSubtitle('Configure SLA rules and rating thresholds');
    fetchData();
  }, [setPageTitle, setPageSubtitle]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesRes, thresholdsRes] = await Promise.all([
        slaService.getAll(),
        slaService.getRatingThresholds()
      ]);
      setSlaRules(rulesRes.data.slaRules || []);
      setRatingThresholds(thresholdsRes.data.ratingThresholds || []);
    } catch (error) {
      console.error('Error fetching SLA data:', error);
      showMessage('Failed to load SLA data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // SLA Rules handlers
  const startEditRule = (rule) => {
    setEditingRule(rule.id);
    setRuleForm({
      priority_label: rule.priority_label,
      first_response_hours: rule.first_response_hours,
      resolution_hours: rule.resolution_hours
    });
  };

  const cancelEditRule = () => {
    setEditingRule(null);
    setRuleForm({});
  };

  const saveRule = async (id) => {
    try {
      setSaving(true);
      await slaService.update(id, ruleForm);
      showMessage('SLA rule updated successfully');
      setEditingRule(null);
      setRuleForm({});
      fetchData();
    } catch (error) {
      console.error('Error updating SLA rule:', error);
      showMessage('Failed to update SLA rule', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Rating Threshold handlers
  const startEditThreshold = (threshold) => {
    setEditingThreshold(threshold.id);
    setThresholdForm({
      min_percentage: threshold.min_percentage,
      color: threshold.color
    });
  };

  const cancelEditThreshold = () => {
    setEditingThreshold(null);
    setThresholdForm({});
  };

  const saveThreshold = async (id) => {
    try {
      setSaving(true);
      await slaService.updateRatingThreshold(id, thresholdForm);
      showMessage('Rating threshold updated successfully');
      setEditingThreshold(null);
      setThresholdForm({});
      fetchData();
    } catch (error) {
      console.error('Error updating rating threshold:', error);
      showMessage('Failed to update rating threshold', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading SLA configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Message Banner */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg border-l-4 ${
          message.type === 'error' 
            ? 'bg-red-50 border-red-500 text-red-800' 
            : 'bg-green-50 border-green-500 text-green-800'
        }`}>
          <p className="text-sm font-medium">{message.text}</p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
        <h4 className="text-sm font-semibold text-blue-800 mb-1">How SLA Ratings Work</h4>
        <p className="text-xs text-blue-700">
          Each ticket's priority (P1/P2/P3) has defined SLA targets for first response and resolution time.
          When a ticket gets its first response or is closed, the system checks if it met the SLA target hours.
          The <strong>Overall Score</strong> = average of (First Response SLA % + Resolution SLA %).
          This score is then mapped to a rating (Excellent/Average/Poor) based on the thresholds below.
        </p>
      </div>

      {/* SLA Rules Table */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">SLA Rules by Priority</h2>
              <p className="text-xs text-gray-500">Define maximum hours for first response and resolution per priority level</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Response (Hours)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolution (Hours)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {slaRules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      rule.priority_code === 'P1' ? 'bg-red-100 text-red-800' :
                      rule.priority_code === 'P2' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {rule.priority_code}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editingRule === rule.id ? (
                      <input
                        type="text"
                        value={ruleForm.priority_label}
                        onChange={(e) => setRuleForm({ ...ruleForm, priority_label: e.target.value })}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm w-24 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <span className="font-medium">{rule.priority_label}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingRule === rule.id ? (
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={ruleForm.first_response_hours}
                        onChange={(e) => setRuleForm({ ...ruleForm, first_response_hours: parseFloat(e.target.value) })}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm w-20 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <span className="text-gray-700 font-medium">{rule.first_response_hours}h</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingRule === rule.id ? (
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={ruleForm.resolution_hours}
                        onChange={(e) => setRuleForm({ ...ruleForm, resolution_hours: parseFloat(e.target.value) })}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm w-20 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <span className="text-gray-700 font-medium">{rule.resolution_hours}h</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {rule.updated_by_name || rule.created_by_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingRule === rule.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveRule(rule.id)}
                          disabled={saving}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEditRule}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditRule(rule)}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rating Thresholds Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Rating Thresholds</h2>
              <p className="text-xs text-gray-500">Define minimum overall score percentage for each rating level</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Minimum Score %</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badge Color</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ratingThresholds.map((threshold) => (
                <tr key={threshold.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    {threshold.rating_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingThreshold === threshold.id ? (
                      <input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={thresholdForm.min_percentage}
                        onChange={(e) => setThresholdForm({ ...thresholdForm, min_percentage: parseFloat(e.target.value) })}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm w-20 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <span className="text-gray-700 font-medium">{threshold.min_percentage}%</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingThreshold === threshold.id ? (
                      <input
                        type="color"
                        value={thresholdForm.color}
                        onChange={(e) => setThresholdForm({ ...thresholdForm, color: e.target.value })}
                        className="w-10 h-8 rounded cursor-pointer border border-gray-300"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full border border-gray-200"
                          style={{ backgroundColor: threshold.color }}
                        ></div>
                        <span className="text-gray-500 text-xs">{threshold.color}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                      style={{
                        backgroundColor: `${threshold.color}20`,
                        color: threshold.color
                      }}
                    >
                      {threshold.rating_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {threshold.updated_by_name || threshold.created_by_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingThreshold === threshold.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveThreshold(threshold.id)}
                          disabled={saving}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEditThreshold}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditThreshold(threshold)}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Explanation */}
      <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
        <h4 className="text-sm font-semibold text-yellow-800 mb-2">Example</h4>
        <div className="text-xs text-yellow-900 space-y-1">
          <p>A P1 ticket created at 10:00 AM with SLA rule: First Response = 2h, Resolution = 8h</p>
          <p>- If first response comes at 11:30 AM (1.5h) → <span className="font-semibold text-green-700">First Response SLA Met</span></p>
          <p>- If ticket closed at 7:00 PM (9h) → <span className="font-semibold text-red-700">Resolution SLA Breached</span> (exceeded 8h)</p>
          <p>- If a region has 80% first response met and 70% resolution met → Overall Score = 75% → <span className="font-semibold text-yellow-700">Average</span></p>
        </div>
      </div>
    </div>
  );
};

export default SLAManagement;
